<template>
  <div id="app">
    <div v-if="showAppShell">
      <div class="layout-light side-menu overlayScroll">
        <div class="mobile-search"></div>
        <div class="mobile-author-actions"></div>
        <Header />
        <main class="main-content">
          <Aside />
          <router-view />
          <Footer />
        </main>
      </div>
      <Floating />
      <SupportWidget />
    </div>
    <div v-else-if="isLoggedIn && fullscreen">
      <router-view />
    </div>
    <div v-else>
      <div class="layout-light side-menu">
        <main class="main-content">
          <router-view />
        </main>
      </div>
    </div>
  </div>
</template>

<script>
import Aside from '@/components/layouts/Aside.vue'
import Header from '@/components/layouts/Header.vue'
import Footer from '@/components/layouts/Footer.vue'
import Floating from '@/components/layouts/Floating.vue'
import SupportWidget from '@/components/layouts/SupportWidget.vue'

export default {
  components: {
    Aside,
    Header,
    Footer,
    Floating,
    SupportWidget
  },
  computed: {
    isPublicRoute() {
      return this.$route.matched.some(record => record.meta?.public);
    },
    showAppShell() {
      return this.isLoggedIn && !this.fullscreen && !this.isPublicRoute;
    },
  },
  watch: {
    '$route'(to) {
      this.fullscreen = !!to.meta?.fullscreen;
      document.title = (to.meta.title ? to.meta.title + " - Starsender V3" : 'Starsender V3');
    }
  },
  data() {
    return {
      connection: null,
      isLoggedIn: this.$store.state.auth.isLoggedIn,
      fullscreen: false,
      // Reconnect state
      retryCount: 0,
      retryTimeout: null,
      // Ping/pong state
      pingInterval: null,
      pongTimeout: null,
      lastPongAt: 0,
      // Flags
      isDestroying: false,
      isManualClose: false,
    };
  },
  mounted() {
    if (localStorage.getItem('socket_token')) {
      this.initWS();
    }

    this.$root.$on("logout", () => {
      this.isLoggedIn = false;
      this.closeWS();
    });

    this.$root.$on("login", () => {
      this.isLoggedIn = true;
      if (localStorage.getItem('socket_token')) {
        this.initWS();
      }
    });

    this.$watch('$route', (to) => {
      if (to.path.includes('/cs-page/web-whatsapp/')) {
        this.fullscreen = true;
      } else {
        this.fullscreen = false;
      }
    });

    // Reconnect when tab becomes visible again
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    // Reconnect when browser goes back online
    window.addEventListener('online', this.onOnline);
    window.addEventListener('offline', this.onOffline);
  },
  beforeDestroy() {
    this.isDestroying = true;
    this.clearAllTimers();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('online', this.onOnline);
    window.removeEventListener('offline', this.onOffline);
    if (this.connection) {
      this.isManualClose = true;
      this.connection.close();
      this.connection = null;
    }
  },
  methods: {
    // ── Visibility & Network handlers ───────────────────────
    onVisibilityChange() {
      if (document.visibilityState === 'visible') {
        // Tab is active again – check if WS is alive
        if (!this.connection || this.connection.readyState !== WebSocket.OPEN) {
          console.log('[WS] Tab visible, reconnecting…');
          this.retryCount = 0; // fresh start when user comes back
          this.scheduleReconnect(0); // immediate
        }
      }
    },
    onOnline() {
      console.log('[WS] Browser online, reconnecting…');
      this.retryCount = 0;
      this.scheduleReconnect(500);
    },
    onOffline() {
      console.log('[WS] Browser offline, pausing reconnect');
      this.clearAllTimers();
    },

    // ── Timer helpers ───────────────────────────────────────
    clearAllTimers() {
      if (this.pingInterval) { clearInterval(this.pingInterval); this.pingInterval = null; }
      if (this.pongTimeout) { clearTimeout(this.pongTimeout); this.pongTimeout = null; }
      if (this.retryTimeout) { clearTimeout(this.retryTimeout); this.retryTimeout = null; }
    },

    // ── Reconnect with exponential backoff ──────────────────
    getBackoffDelay() {
      // 1s, 2s, 4s, 8s, 16s, capped at 30s
      const base = 1000;
      const delay = Math.min(base * Math.pow(2, this.retryCount), 30000);
      // Add small jitter (±20%) to prevent thundering herd
      const jitter = delay * 0.2 * (Math.random() - 0.5);
      return Math.round(delay + jitter);
    },

    scheduleReconnect(overrideDelay) {
      if (this.isDestroying) return;
      if (!navigator.onLine) return; // don't retry while offline

      if (this.retryTimeout) {
        clearTimeout(this.retryTimeout);
      }

      const delay = overrideDelay !== undefined ? overrideDelay : this.getBackoffDelay();
      console.log(`[WS] Reconnect in ${delay}ms (attempt #${this.retryCount + 1})`);

      this.retryTimeout = setTimeout(() => {
        this.retryTimeout = null;
        this.initWS();
      }, delay);
    },

    // ── Close WS gracefully ─────────────────────────────────
    closeWS() {
      this.isManualClose = true;
      this.clearAllTimers();
      if (this.connection) {
        this.connection.close();
        this.connection = null;
      }
    },

    // ── Main WS init ────────────────────────────────────────
    initWS() {
      // Guard: don't connect if destroying or no token
      if (this.isDestroying) return;
      const socketToken = localStorage.getItem('socket_token');
      if (!socketToken) return;

      // Guard: already connected
      if (this.connection && this.connection.readyState === WebSocket.OPEN) {
        return;
      }

      // Guard: currently connecting
      if (this.connection && this.connection.readyState === WebSocket.CONNECTING) {
        return;
      }

      // Clean up previous broken connection
      if (this.connection) {
        try { this.connection.close(); } catch(e) { /* ignore */ }
        this.connection = null;
      }

      this.isManualClose = false;
      this.clearAllTimers();

      console.log('[WS] Connecting…');

      try {
        this.connection = new WebSocket(process.env.VUE_APP_WS_URL);
      } catch (err) {
        console.error('[WS] Failed to create WebSocket:', err);
        this.retryCount++;
        this.scheduleReconnect();
        return;
      }

      // ── onopen ──
      this.connection.onopen = () => {
        console.log('[WS] Connected!');
        this.retryCount = 0;

        // Send init payload
        if (this.connection && this.connection.readyState === WebSocket.OPEN) {
          try {
            this.connection.send(JSON.stringify({
              event: "init",
              socket_token: socketToken,
            }));
          } catch (err) {
            console.error('[WS] Init send error:', err);
          }
        }

        // Start ping/pong heartbeat
        this.startHeartbeat();
      };

      // ── onmessage ──
      this.connection.onmessage = (event) => {
        try {
          const data = event.data;
          // Handle pong response
          if (data === 'pong') {
            this.lastPongAt = Date.now();
            if (this.pongTimeout) {
              clearTimeout(this.pongTimeout);
              this.pongTimeout = null;
            }
          }
          // Broadcast to all listeners
          this.$root.$emit("ws", data);
        } catch (err) {
          console.error('[WS] onmessage error:', err);
        }
      };

      // ── onerror ──
      this.connection.onerror = (err) => {
        console.error('[WS] Error:', err);
        // onclose will fire after this, which handles reconnect
      };

      // ── onclose ──
      this.connection.onclose = (event) => {
        console.log(`[WS] Closed (code: ${event.code}, reason: ${event.reason || 'none'})`);

        this.clearAllTimers();
        this.connection = null;

        // Don't reconnect if it was a manual/intentional close
        if (this.isManualClose || this.isDestroying) return;

        // Schedule reconnect with backoff
        this.retryCount++;
        this.scheduleReconnect();
      };
    },

    // ── Heartbeat: ping every 25s, expect pong within 10s ──
    startHeartbeat() {
      if (this.pingInterval) clearInterval(this.pingInterval);
      if (this.pongTimeout) clearTimeout(this.pongTimeout);

      this.lastPongAt = Date.now();

      this.pingInterval = setInterval(() => {
        if (!this.connection || this.connection.readyState !== WebSocket.OPEN) {
          this.clearAllTimers();
          return;
        }

        try {
          this.connection.send('ping');
        } catch (err) {
          console.error('[WS] Ping send error:', err);
          this.clearAllTimers();
          return;
        }

        // If no pong within 10s → connection is dead, force reconnect
        this.pongTimeout = setTimeout(() => {
          console.warn('[WS] Pong timeout – connection seems dead, reconnecting…');
          this.clearAllTimers();
          if (this.connection) {
            try { this.connection.close(); } catch(e) { /* ignore */ }
            this.connection = null;
          }
          this.retryCount = 0;
          this.scheduleReconnect(0);
        }, 10000);
      }, 25000);
    },
  },
};
</script>



<style>
.spinner {
  height: 1rem !important;
  width: 1rem !important;
}

.vm--modal {
  height: auto !important;
  max-height: 95% !important;
  overflow-y: auto !important;
}

@media only screen and (max-device-width: 610px) {
  .vm--modal {
    width: 94% !important;
    margin-left: 3% !important;
    margin-right: 3% !important;
  }
}

hr {
  margin-top: 20px !important;
  margin-bottom: 20px !important;
}
</style>